import { Plus, Tag as TagIcon, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import type { Tag } from '../api';
import { resolveTagColor, tagChipStyle, tagColorForName } from '../colors';
import {
  useAddTag,
  useCreateTag,
  useEntityTags,
  useRemoveTag,
  useTagVocabulary,
} from '../queries';

interface TagsPanelProps {
  /** Django model label, e.g. "crm.deal". */
  modelLabel: string;
  objectId: number;
}

/** A coloured dot for a tag (uses its resolved palette colour). */
function Dot({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      className="size-2 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
  );
}

/** Org-scoped tags for one CRM entity. Reuses the generic collab Tag /
 *  TaggedItem system — works for any model label, no per-entity schema. */
export function TagsPanel({ modelLabel, objectId }: TagsPanelProps) {
  const { contentTypeId, links, isPending, isError } = useEntityTags(modelLabel, objectId);
  const vocabulary = useTagVocabulary();
  const add = useAddTag(modelLabel, objectId);
  const remove = useRemoveTag(modelLabel, objectId);
  const createTag = useCreateTag();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const byId = useMemo(() => {
    const map = new Map<number, Tag>();
    for (const t of vocabulary.data ?? []) map.set(t.id, t);
    return map;
  }, [vocabulary.data]);

  const attached = links.data ?? [];
  const attachedIds = new Set(attached.map((l) => l.tag));

  const q = query.trim().toLowerCase();
  const available = (vocabulary.data ?? [])
    .filter((t) => !attachedIds.has(t.id) && t.name.toLowerCase().includes(q));
  const exactMatch = (vocabulary.data ?? []).some((t) => t.name.toLowerCase() === q);
  const canCreate = q.length > 0 && !exactMatch;

  const busy = add.isPending || createTag.isPending;

  async function attachExisting(tagId: number) {
    if (!contentTypeId) return;
    await add.mutateAsync({ tag: tagId, content_type: contentTypeId });
    setQuery('');
  }

  async function createAndAttach() {
    if (!contentTypeId || !query.trim()) return;
    const name = query.trim();
    const tag = await createTag.mutateAsync({ name, color: tagColorForName(name) });
    await add.mutateAsync({ tag: tag.id, content_type: contentTypeId });
    setQuery('');
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Tags</CardTitle>
          <CardDescription>Labels for segmenting this {modelLabel.split('.')[1]}.</CardDescription>
        </div>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline" disabled={!contentTypeId || isError}>
              <Plus className="size-4" /> Add tag
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="end">
            <Command shouldFilter={false}>
              <CommandInput placeholder="Search or create…" value={query} onValueChange={setQuery} />
              <CommandList>
                {available.length === 0 && !canCreate ? (
                  <div className="text-muted-foreground p-3 text-sm">
                    {vocabulary.isPending ? 'Loading…' : 'No tags yet.'}
                  </div>
                ) : null}
                {available.length > 0 ? (
                  <CommandGroup heading="Existing">
                    {available.map((t) => (
                      <CommandItem
                        key={t.id}
                        value={`existing-${t.id}`}
                        disabled={busy}
                        onSelect={() => attachExisting(t.id)}
                      >
                        <Dot color={resolveTagColor(t)} />
                        {t.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ) : null}
                {canCreate ? (
                  <CommandGroup heading="Create">
                    <CommandItem value="__create__" disabled={busy} onSelect={createAndAttach}>
                      <Plus className="size-4" /> Create “{query.trim()}”
                    </CommandItem>
                  </CommandGroup>
                ) : null}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </CardHeader>
      <CardContent>
        {isError ? (
          <p className="text-destructive text-sm">Failed to load tags.</p>
        ) : isPending ? (
          <div className="flex gap-2">
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
        ) : attached.length === 0 ? (
          <p className="text-muted-foreground flex items-center gap-2 text-sm">
            <TagIcon className="size-4" /> No tags yet.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {attached.map((link) => {
              const tag = byId.get(link.tag);
              const color = tag ? resolveTagColor(tag) : '#64748b';
              return (
                <li key={link.id}>
                  <Badge variant="outline" className="gap-1.5 pr-1" style={tagChipStyle(color)}>
                    {tag?.name ?? '…'}
                    <button
                      type="button"
                      aria-label={`Remove tag ${tag?.name ?? ''}`}
                      className="ml-0.5 rounded-full p-0.5 hover:bg-black/10"
                      onClick={() => remove.mutate(link.id)}
                      disabled={remove.isPending}
                    >
                      <X className="size-3" />
                    </button>
                  </Badge>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
